import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  useRecordings,
  useRecordingDetail,
  fetchVersionBody,
  useVersionBody,
  patchVersionVisibility,
  fetchAudioUrl,
  fetchVersionPdfUrl,
  submitRecordingApi,
  type Recording,
  type TranscriptSegment,
  type RecordingSpeaker,
} from "../api/transcription";
import { formatLocal } from "../components/formatting";
import { useAuth } from "../auth/useAuth";

const SPEAKER_COLOR_COUNT = 6;

function speakerColorClass(label: string, speakers: RecordingSpeaker[]): string {
  const idx = speakers.findIndex((s) => s.speaker_label === label);
  const n = (idx >= 0 ? idx : label.charCodeAt(0)) % SPEAKER_COLOR_COUNT;
  return `tr-speaker-${n}`;
}

function speakerName(label: string, speakers: RecordingSpeaker[]): string {
  return speakers.find((s) => s.speaker_label === label)?.person_name ?? `Speaker ${label}`;
}

function mmss(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function confidenceClass(confidence: number | null): string {
  if (confidence == null) return "";
  if (confidence < 0.5) return "tr-conf-low";
  if (confidence < 0.7) return "tr-conf-mid";
  return "";
}

function downloadText(filename: string, text: string): void {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function StatusBadge({ status }: { status: Recording["status"] }) {
  return <span className={`tr-status tr-status-${status}`}>{status}</span>;
}

// Stamp a timestamp chip at most once per minute of audio.
function withStamps(segments: TranscriptSegment[]): { seg: TranscriptSegment; stamp: string | null }[] {
  let last = -Infinity;
  return segments.map((seg) => {
    const stamp = seg.start_ms - last >= 60_000 ? mmss(seg.start_ms) : null;
    if (stamp) last = seg.start_ms;
    return { seg, stamp };
  });
}

// Turn every audio timestamp in the markdown into a seek link, in all the shapes
// the summaries use: bracketed ranges [2:15–3:19], bracketed singles [2:15], and
// bare times in prose ("at 28:50"). Clock times ("11:00 AM") are excluded.
function linkifyStamps(text: string): string {
  const link = (t: string) => {
    const [m, s] = t.split(":");
    return `[${t}](#seek-${m}-${s})`;
  };
  let out = text.replace(
    /\[(\d{1,2}:\d{2})\s*[–—-]\s*(\d{1,2}:\d{2}|end)\]/g,
    (_all, a: string, b: string) => `${link(a)}–${b === "end" ? "end" : link(b)}`
  );
  out = out.replace(/\[(\d{1,2}:\d{2})\]/g, (_all, a: string) => link(a));
  out = out.replace(
    /(?<![[\d:])(\d{1,2}:\d{2})(?![\d:])(?!\s*[APap]\.?[Mm])/g,
    (_all, a: string) => link(a)
  );
  return out;
}

// Renders a stored version's markdown with [m:ss] stamps as clickable audio seeks.
function VersionDoc({
  recordingId,
  version,
  onSeek,
}: {
  recordingId: string;
  version: number;
  onSeek: (ms: number) => void;
}) {
  const { data, isLoading } = useVersionBody(recordingId, version);
  const processed = useMemo(() => (data ? linkifyStamps(data.body_text) : ""), [data]);
  if (isLoading) return <div className="tr-detail-empty">Loading version…</div>;
  if (!data) return <div className="tr-detail-empty">Failed to load version.</div>;
  return (
    <div className="tr-doc">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            const m = href?.match(/^#seek-(\d+)-(\d\d)$/);
            if (m) {
              const ms = (parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) * 1000;
              return (
                <a
                  href={href}
                  className="tr-ts tr-ts-link"
                  onClick={(e) => {
                    e.preventDefault();
                    onSeek(ms);
                  }}
                >
                  {children}
                </a>
              );
            }
            return (
              <a href={href} target="_blank" rel="noreferrer">
                {children}
              </a>
            );
          },
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}

function RecordingDetailPane({ id, familyMode }: { id: string; familyMode: boolean }) {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState<"live" | number>("live");
  const { data, isLoading, error } = useRecordingDetail(id);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stamped = useMemo(() => (data ? withStamps(data.segments) : []), [data]);

  if (isLoading) return <div className="tr-detail-empty">Loading…</div>;
  if (error || !data) return <div className="tr-detail-empty">Failed to load recording.</div>;

  const { recording, versions, segments, speakers } = data;
  const lowConfCount = segments.filter((s) => s.confidence != null && s.confidence < 0.7).length;

  async function loadAudio(): Promise<void> {
    if (audioUrl) return;
    setAudioLoading(true);
    try {
      const { url } = await fetchAudioUrl(recording.id);
      setAudioUrl(url);
    } finally {
      setAudioLoading(false);
    }
  }

  // Seek from anywhere (segment click or a version's [m:ss] stamp); loads the
  // audio first if it isn't loaded yet.
  async function seekTo(ms: number) {
    await loadAudio();
    setTimeout(() => {
      const el = audioRef.current;
      if (!el) return;
      el.currentTime = ms / 1000;
      void el.play();
    }, 150);
  }

  // Family accounts have no live segment view; default them onto the newest
  // shared version instead.
  const effectiveView: "live" | number =
    familyMode && view === "live"
      ? (versions[versions.length - 1]?.version ?? "live")
      : view;

  const selectedVersion =
    effectiveView === "live"
      ? null
      : (versions.find((v) => v.version === effectiveView) ?? null);

  async function toggleVisibility() {
    if (!selectedVersion) return;
    await patchVersionVisibility(
      recording.id,
      selectedVersion.version,
      selectedVersion.visibility === "family" ? "private" : "family"
    );
    await queryClient.invalidateQueries({
      queryKey: ["transcription-recording-detail", recording.id],
    });
  }

  async function downloadVersion(version: number, kind: string) {
    const { body_text } = await fetchVersionBody(recording.id, version);
    const ext = kind === "raw_assembly" ? "txt" : "md";
    downloadText(`${recording.title} - ${kind} v${version}.${ext}`, body_text);
  }

  return (
    <div className="tr-detail">
      <div className="tr-detail-header">
        <h2>{recording.title}</h2>
        <div className="tr-meta">
          <StatusBadge status={recording.status} />
          <span>{recording.recorded_at ? formatLocal(recording.recorded_at) : "date unknown"}</span>
          <span>{formatDuration(recording.duration_seconds)}</span>
          {recording.asr_provider && <span className="tr-provider">{recording.asr_provider}</span>}
        </div>
        {recording.error && !familyMode && <div className="tr-error">{recording.error}</div>}
        {!familyMode && (recording.status === "uploaded" || recording.status === "failed") && (
          <div className="tr-toolbar">
            <button
              className="btn-small tr-submit"
              disabled={submitting}
              onClick={async () => {
                setSubmitting(true);
                try {
                  await submitRecordingApi(recording.id);
                  await queryClient.invalidateQueries({
                    queryKey: ["transcription-recording-detail", recording.id],
                  });
                  await queryClient.invalidateQueries({ queryKey: ["transcription-recordings"] });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {submitting
                ? "Submitting…"
                : recording.status === "failed"
                  ? "↻ Retry transcription"
                  : "▶ Submit for transcription"}
            </button>
          </div>
        )}
        <div className="tr-toolbar">
          <select
            className="tr-view-select"
            value={effectiveView === "live" ? "live" : String(effectiveView)}
            onChange={(e) =>
              setView(e.target.value === "live" ? "live" : parseInt(e.target.value, 10))
            }
          >
            {!familyMode && (
              <option value="live">Live transcript (segments + confidence)</option>
            )}
            {versions.map((v) => (
              <option key={v.id} value={v.version}>
                v{v.version} · {v.kind}
                {!familyMode && v.visibility === "family" ? " · 👥 family" : ""}
              </option>
            ))}
          </select>
          {selectedVersion && (
            <>
              {!familyMode && (
                <button
                  className="btn-small"
                  title={selectedVersion.produced_by}
                  onClick={() =>
                    void downloadVersion(selectedVersion.version, selectedVersion.kind)
                  }
                >
                  ↓ Markdown
                </button>
              )}
              {selectedVersion.pdf_s3_key && (
                <button
                  className="btn-small"
                  onClick={async () => {
                    const { url } = await fetchVersionPdfUrl(
                      recording.id,
                      selectedVersion.version
                    );
                    window.open(url, "_blank", "noopener");
                  }}
                >
                  ↓ PDF
                </button>
              )}
              {!familyMode && (
                <button
                  className="btn-small"
                  title="Whether this version will be visible to family accounts (future access layer)"
                  onClick={() => void toggleVisibility()}
                >
                  {selectedVersion.visibility === "family" ? "👥 family-visible" : "🔒 private"}
                </button>
              )}
            </>
          )}
          {recording.s3_key_audio && !audioUrl && (
            <button className="btn-small" onClick={() => void loadAudio()} disabled={audioLoading}>
              {audioLoading ? "Loading audio…" : "▶ Load audio"}
            </button>
          )}
        </div>
        {audioUrl && <audio ref={audioRef} controls src={audioUrl} className="tr-audio" />}
        {speakers.length > 0 && (
          <div className="tr-speakers">
            {speakers.map((s) => (
              <span key={s.speaker_label} className={`tr-speaker-chip ${speakerColorClass(s.speaker_label, speakers)}`}>
                {s.speaker_label} = {s.person_name}
                {s.confirmed_by && <em> ({s.confirmed_by})</em>}
              </span>
            ))}
          </div>
        )}
        {effectiveView === "live" && segments.length > 0 && (
          <div className="tr-conf-note">
            {lowConfCount} of {segments.length} segments below 70% confidence (highlighted).
            Click a segment or timestamp to jump the audio.
          </div>
        )}
      </div>

      {effectiveView === "live" ? (
        <div className="tr-transcript">
          {stamped.length === 0 && (
            <div className="tr-detail-empty">No transcript segments yet.</div>
          )}
          {stamped.map(({ seg, stamp }) => (
            <p
              key={seg.idx}
              className={`tr-utterance ${confidenceClass(seg.confidence)} tr-clickable`}
              onClick={() => void seekTo(seg.start_ms)}
              title={seg.confidence != null ? `confidence ${(seg.confidence * 100).toFixed(0)}% · ${mmss(seg.start_ms)}` : mmss(seg.start_ms)}
            >
              {stamp && <span className="tr-ts">{stamp}</span>}
              <strong className={speakerColorClass(seg.speaker_label, speakers)}>
                {speakerName(seg.speaker_label, speakers)}:
              </strong>{" "}
              {seg.text}
            </p>
          ))}
        </div>
      ) : (
        <VersionDoc
          recordingId={recording.id}
          version={effectiveView as number}
          onSeek={(ms) => void seekTo(ms)}
        />
      )}
    </div>
  );
}

export function TranscriptsView() {
  const auth = useAuth();
  const familyMode = auth.status === "authenticated" && auth.role === "family";
  const { data, isLoading } = useRecordings();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const items = data?.items ?? [];
  const selected = selectedId ?? items[0]?.id ?? null;

  return (
    <div className="tr-layout">
      <aside className="tr-list">
        <h2>Recordings</h2>
        {isLoading && <div className="tr-detail-empty">Loading…</div>}
        {!isLoading && items.length === 0 && (
          <div className="tr-detail-empty">No recordings yet.</div>
        )}
        {items.map((r) => (
          <button
            key={r.id}
            className={`tr-list-item ${selected === r.id ? "tr-list-item-active" : ""}`}
            onClick={() => setSelectedId(r.id)}
          >
            <span className="tr-list-title">{r.title}</span>
            <span className="tr-list-meta">
              <StatusBadge status={r.status} />
              <span>{formatDuration(r.duration_seconds)}</span>
              <span>{r.recorded_at ? formatLocal(r.recorded_at).split(",")[0] : ""}</span>
            </span>
          </button>
        ))}
      </aside>
      <section className="tr-main">
        {selected ? (
          <RecordingDetailPane key={selected} id={selected} familyMode={familyMode} />
        ) : (
          <div className="tr-detail-empty">Select a recording.</div>
        )}
      </section>
    </div>
  );
}
